/**
 * Camera acquisition. The rear camera is the default because it has the better
 * sensor and the phone is meant to stand beside the desk facing the user's side
 * (ARCHITECTURE §1.1); the preview lets them switch if the phone is placed the
 * other way round.
 */
export type CameraFacing = 'environment' | 'user';

export class CameraError extends Error {
  constructor(
    message: string,
    readonly code: 'camera_denied' | 'camera_missing' | 'camera_busy' | 'insecure_context',
  ) {
    super(message);
    this.name = 'CameraError';
  }
}

export interface CameraStream {
  stream: MediaStream;
  facing: CameraFacing;
  stop(): void;
}

/**
 * Portrait 4:3, not 16:9 — and this matters a lot.
 *
 * Phone sensors are natively 4:3. Asking for a 9:16 portrait frame makes the
 * browser *crop the sensor* to that shape, which throws away field of view: the
 * subject fills the frame and you have to put the phone absurdly far away to
 * fit a seated person from the side. Asking for 3:4 uses the full sensor width
 * and gives the widest view that lens can produce.
 *
 * Requested as `ideal`, so a device that cannot do it picks the closest mode;
 * the metrics layer reads the real dimensions back and corrects every angle for
 * the actual aspect ratio.
 */
export const PORTRAIT_IDEAL = { width: 960, height: 1280 } as const;

/**
 * Pick the widest rear lens available.
 *
 * `facingMode: 'environment'` gives whatever the browser considers the default
 * back camera — on recent iPhones that is the standard lens, not the ultra-wide
 * one. For framing a seated person from a metre and a half away, the wider lens
 * is the difference between a usable setup and pushing the phone against the
 * far wall. Labels are only populated after permission has been granted once,
 * so this is best-effort and falls back to `facingMode`.
 */
async function preferredDeviceId(facing: CameraFacing): Promise<string | null> {
  if (facing !== 'environment') return null;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((d) => d.kind === 'videoinput' && d.label);
    if (cameras.length <= 1) return null;
    const rear = cameras.filter((d) => /back|rear|environment|후면/i.test(d.label));
    const pool = rear.length > 0 ? rear : cameras;
    const ultra = pool.find((d) => /ultra|0\.5|초광각/i.test(d.label));
    if (ultra) return ultra.deviceId;
    const wide = pool.find((d) => /wide/i.test(d.label) && !/tele/i.test(d.label));
    return wide?.deviceId ?? null;
  } catch {
    return null;
  }
}

export async function openCamera(facing: CameraFacing = 'environment'): Promise<CameraStream> {
  if (!window.isSecureContext) {
    throw new CameraError(
      'The camera needs a secure connection (https or localhost).',
      'insecure_context',
    );
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CameraError('This browser cannot open a camera.', 'camera_missing');
  }
  try {
    const deviceId = await preferredDeviceId(facing);
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: facing }),
        width: { ideal: PORTRAIT_IDEAL.width },
        height: { ideal: PORTRAIT_IDEAL.height },
        aspectRatio: { ideal: PORTRAIT_IDEAL.width / PORTRAIT_IDEAL.height },
        frameRate: { ideal: 15, max: 30 },
      },
      audio: false,
    });
    // Some lenses expose optical zoom and default to something above 1×.
    // Pull it back to the widest setting the track allows.
    await widenIfZoomed(stream);

    return {
      stream,
      facing,
      stop() {
        for (const track of stream.getTracks()) track.stop();
      },
    };
  } catch (error) {
    throw toCameraError(error);
  }
}

/** Reset any zoom the platform applied, so we get the full field of view. */
async function widenIfZoomed(stream: MediaStream): Promise<void> {
  const track = stream.getVideoTracks()[0];
  if (!track) return;
  try {
    const capabilities = track.getCapabilities?.() as { zoom?: { min?: number } } | undefined;
    const min = capabilities?.zoom?.min;
    if (typeof min !== 'number') return;
    const settings = track.getSettings() as { zoom?: number };
    if (settings.zoom !== undefined && settings.zoom > min) {
      // `zoom` is a standard-track extension that lib.dom does not type yet.
      await track.applyConstraints({ advanced: [{ zoom: min }] } as unknown as MediaTrackConstraints);
    }
  } catch {
    // Zoom control is optional; the aspect-ratio fix above does the heavy lifting.
  }
}

function toCameraError(error: unknown): CameraError {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new CameraError(
        'Camera access was blocked. Allow the camera in your browser settings and reload.',
        'camera_denied',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new CameraError('No usable camera was found on this device.', 'camera_missing');
    case 'NotReadableError':
    case 'AbortError':
      return new CameraError(
        'The camera is already in use by another app. Close it and try again.',
        'camera_busy',
      );
    default:
      return new CameraError('The camera could not be started.', 'camera_missing');
  }
}

/** Attach a stream to a <video> and wait until it has real dimensions. */
export async function attachStream(
  video: HTMLVideoElement,
  stream: MediaStream,
): Promise<void> {
  video.srcObject = stream;
  video.muted = true;
  video.playsInline = true;
  await video.play();
  if (video.videoWidth > 0) return;
  await new Promise<void>((resolve) => {
    const onReady = (): void => {
      video.removeEventListener('loadedmetadata', onReady);
      resolve();
    };
    video.addEventListener('loadedmetadata', onReady);
  });
}
